import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useParentOverview } from './use-parent-overview';
import { PageContainer } from '@/components/PageContainer';

/**
 * The frame every parent page sits in.
 *
 * A guardian moves between four jobs — decide, oversee, vouch, adjust — and
 * before this each lived on its own island. One rail across all of them turns
 * a set of pages into a console, and carries the counts so nothing waiting is
 * ever more than a glance away.
 */

interface Tab {
  label: string;
  to: string;
  badge?: number;
}

export function ParentShell({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const overview = useParentOverview();
  const data = overview.data;

  const tabs: Tab[] = [
    { label: 'Overview', to: '/parent' },
    { label: 'Their shortlist', to: '/teen-shortlist' },
    { label: 'Goals', to: '/goals' },
    { label: 'Insights', to: '/insights' },
    { label: 'Live', to: '/tracking' },
    { label: 'Money', to: '/money' },
    { label: 'Job approvals', to: '/approvals', badge: data?.pendingJobApprovals ?? 0 },
    { label: 'Teen accounts', to: '/guardian', badge: data?.pendingTeenAccounts ?? 0 },
    {
      label: 'Trusted network',
      to: '/network',
      badge: data?.incomingConnectionRequests ?? 0,
    },
    { label: 'Settings', to: '/settings' },
  ];

  return (
    <PageContainer>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.14em] text-brand-600 uppercase">
            Parent console
          </p>
          <h1 className="mt-2 text-2xl font-extrabold text-ink sm:text-3xl">{title}</h1>
          {description && (
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">{description}</p>
          )}
        </div>
        {action}
      </header>

      <nav
        aria-label="Parent console"
        className="mt-6 -mx-1 flex gap-1 overflow-x-auto border-b border-line pb-px"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end
            className={({ isActive }) =>
              `relative flex shrink-0 items-center gap-2 rounded-t-lg px-4 py-3 text-sm font-semibold whitespace-nowrap transition ${
                isActive
                  ? 'text-brand-700 after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand-600'
                  : 'text-slate-500 hover:text-ink'
              }`
            }
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
                {tab.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-8">{children}</div>
    </PageContainer>
  );
}

